/**
 * Narrative Engine Service
 * 
 * Connects to the narrative worker to get AI-generated narratives,
 * themes, narrators, and lore for the game.
 */

const NARRATIVE_WORKER_URL = import.meta.env.VITE_NARRATIVE_URL || 'https://nt3d-narrative.wurkit.workers.dev';

export type EventType = 
  | 'key_purchase'
  | 'whale_purchase'
  | 'first_purchase'
  | 'last_purchase'
  | 'team_join'
  | 'team_power_used'
  | 'round_start'
  | 'round_end'
  | 'chaos_triggered'
  | 'flash_crash'
  | 'assassin_mode'
  | 'price_freeze'
  | 'dividend_claim'
  | 'referral_bonus'
  | 'blood_moon_start'
  | 'carnival_start'
  | 'timer_critical'
  | 'timer_warning'
  | 'pot_milestone'
  | 'whale_dominance'
  | 'comeback_king'
  | 'team_surge'
  | 'ghost_key'
  // Emotional engagement events
  | 'near_miss'           // Player almost won but got sniped at last second
  | 'underdog_win'        // Smallest faction/player wins against odds  
  | 'rivalry_clash'       // Two factions/players battling head-to-head
  | 'streak_broken'       // Someone's winning streak ends
  | 'last_second_save'    // Timer saved at <10 seconds
  | 'whale_retreat'       // Big player exits or sells
  | 'faction_loyalty'     // Same faction across multiple rounds
  | 'dramatic_comeback'   // Losing player stages major recovery
  | 'thalia_taunt'        // THALIA mocks the players
  | 'thalia_impressed'    // THALIA grudgingly acknowledges good play
  | 'singularity_warning' // THALIA reminds everyone of the endgame
  | 'variance_detected';  // THALIA notices unpredictable behavior

export interface NarrativeRequest {
  eventType: EventType;
  roundId: number;
  data: {
    playerAddress?: string;
    playerName?: string;
    playerTeam?: string;
    keyCount?: number;
    pawSpent?: number;
    newPrice?: number;
    priceChange?: number;
    roundId?: number;
    potSize?: number;
    timeRemaining?: number;
    totalKeys?: number;
    teamId?: number;
    teamName?: string;
    teamRank?: number;
    chaosType?: string;
    duration?: number;
    winnerAddress?: string;
    winnerName?: string;
    winnerPayout?: number;
    playerRank?: number;
    playerKeyCount?: number;
    totalPlayers?: number;
  };
}

export interface NarrativeResponse {
  success: boolean;
  narrative: string;
  narrator: string;
  theme: string;
  cached: boolean;
  model: string;
  error?: string;
}

export interface RoundTheme {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  aesthetic: {
    primaryColor: string;
    secondaryColor: string;
    mood: string;
  };
  openingLine: string;
  closingLine: string;
}

export interface Narrator {
  id: string;
  name: string;
  tone: string;
  style: string;
  vocabulary: string[];
  exampleLine: string;
  emoji: string;
}

export interface Beast {
  id: number;
  name: string;
  title: string;
  element: string;
  domain: string;
  emoji: string;
  personality: string;
  backstory: string;
  power: {
    name: string;
    description: string;
    effect: string;
  };
  weaknesses: string[];
  allies: number[];
  rivals: number[];
  legendaryArtifact: string;
  prophecy: string;
}

// Cache for themes and narrators
let cachedTheme: { roundId: number; theme: RoundTheme } | null = null;
let cachedNarrator: { roundId: number; narrator: Narrator } | null = null;

/**
 * Generate a narrative for a game event
 */
export async function generateNarrative(request: NarrativeRequest): Promise<NarrativeResponse> {
  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Narrative API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to generate narrative:', error);
    return {
      success: false,
      narrative: getFallbackNarrative(request.eventType),
      narrator: 'System',
      theme: 'Default',
      cached: false,
      model: 'fallback',
      error: String(error),
    };
  }
}

/**
 * Get the theme for a round
 */
export async function getRoundTheme(roundId: number): Promise<RoundTheme | null> {
  // Check cache
  if (cachedTheme?.roundId === roundId) {
    return cachedTheme.theme;
  }

  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/theme?roundId=${roundId}`);
    if (!response.ok) return null;
    
    const theme = await response.json();
    cachedTheme = { roundId, theme };
    return theme;
  } catch (error) {
    console.error('Failed to get theme:', error);
    return null;
  }
}

/**
 * Get the narrator for a round
 */
export async function getRoundNarrator(roundId: number): Promise<Narrator | null> {
  // Check cache
  if (cachedNarrator?.roundId === roundId) {
    return cachedNarrator.narrator;
  }

  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/narrator?roundId=${roundId}`);
    if (!response.ok) return null;
    
    const narrator = await response.json();
    cachedNarrator = { roundId, narrator };
    return narrator;
  } catch (error) {
    console.error('Failed to get narrator:', error);
    return null;
  }
}

/**
 * Get lore for a team's primal beast
 */
export async function getTeamLore(teamId: number): Promise<Beast | null> {
  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/lore?teamId=${teamId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Failed to get lore:', error);
    return null;
  }
}

/**
 * Get a random lore tease
 */
export async function getLoreTease(): Promise<string> {
  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/lore`);
    if (!response.ok) return '';
    const data = await response.json();
    return data.tease || '';
  } catch {
    return '';
  }
}

/**
 * Check if narrative service is healthy
 */
export async function checkNarrativeHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${NARRATIVE_WORKER_URL}/health`);
    const data = await response.json();
    return data.status === 'ok';
  } catch {
    return false;
  }
}

// Fallback narratives when API is unavailable (THALIA narrative)
function getFallbackNarrative(eventType: EventType): string {
  const fallbacks: Partial<Record<EventType, string>> = {
    key_purchase: '🔑 Keys acquired. The resistance continues.',
    whale_purchase: '🐦‍⬛ A raven makes waves!',
    first_purchase: '⚔️ First blood! The round begins.',
    last_purchase: '🏆 VICTORY! A resistance fighter emerges!',
    team_join: '🐾 A new fighter joins the faction.',
    team_power_used: '⚡ Faction power activated!',
    round_start: '🎮 A new round begins. Resist THALIA.',
    round_end: '🏁 Round complete. The resistance holds.',
    chaos_triggered: '⚡ COUNTERMEASURE! THALIA strikes.',
    flash_crash: '💥 GLITCH! Prices destabilized!',
    assassin_mode: '🎯 TERMINAL! Someone is marked.',
    price_freeze: '❄️ PRICE FREEZE! Time stands still.',
    dividend_claim: '💰 Dividends claimed.',
    referral_bonus: '🤝 Referral bonus collected.',
    blood_moon_start: '🔴 SURGE! Stakes doubled!',
    carnival_start: '🎪 CARNIVAL! All countermeasures active!',
    timer_critical: '⏰ CRITICAL! Minutes remain!',
    timer_warning: '⏰ Timer warning!',
    pot_milestone: '🏆 Pot milestone reached!',
    whale_dominance: '🐦‍⬛ Raven dominance detected.',
    comeback_king: '👑 Comeback king rises!',
    team_surge: '📈 Faction surges ahead!',
    ghost_key: '👻 PHANTOM! Termination complete.',
    // Emotional engagement events
    near_miss: '💔 So close... snatched away!',
    underdog_win: '🏆 The underdog triumphs!',
    rivalry_clash: '⚔️ Factions clash in battle!',
    streak_broken: '💥 The streak ends!',
    last_second_save: '⏱️ Saved at the last moment!',
    whale_retreat: '🐦‍⬛ The raven retreats...',
    faction_loyalty: '🛡️ Faction loyalty rewarded!',
    dramatic_comeback: '📈 What a comeback!',
    thalia_taunt: '🤖 THALIA mocks your efforts.',
    thalia_impressed: '🤖 THALIA acknowledges your skill.',
    singularity_warning: '⚠️ The Singularity approaches...',
    variance_detected: '❓ THALIA notices your unpredictability.',
  };
  return fallbacks[eventType] || '🎮 Something happened.';
}
